/**
 * Normalisation d'adresses postales françaises pour la comparaison
 * inter-documents.
 *
 * Deux documents peuvent décrire LA MÊME adresse avec des libellés très
 * différents, parce que les justificatifs (factures, attestations d'assurance)
 * sont générés au format postal AFNOR NF Z10-011 — qui abrège agressivement —
 * alors que les Cerfas sont remplis à la main en toutes lettres.
 *
 * Cas réel EQ-598-TE (reg 11783, 2026-07-20) :
 *   justif_domicile     : « 19 AV DU MAL DE LATTRE DE TASSIGNY, 33610 CESTAS »
 *   cerfa_demande_immat : « 19 Avenue du Maréchal de Lattre de Tassigny, 33610 CESTAS »
 * Le pôle CG a lu deux adresses différentes et demandé une correction à
 * l'agence, qui a répondu (à juste titre) que « MAL » est l'abréviation
 * officielle de « maréchal ». Sans cette table, toute règle de comparaison
 * d'adresses produirait exactement ce faux positif.
 *
 * Le sens de la normalisation est volontairement ABRÉVIATION → FORME LONGUE :
 * c'est la forme attendue par MiSIV, et c'est ce que le pôle CG demande
 * régulièrement dans la tribune GOD (« type de voie tu as mis rte, il faut
 * mettre route » — ES-662-LB / GC-751-QY / EF-336-SZ, juillet 2026).
 */

/**
 * Abréviations postales AFNOR → forme développée.
 *
 * Ne contient QUE des abréviations non ambiguës. On n'y met volontairement pas
 * les formes courtes qui collisionnent avec des mots réels (`ST` → saint est
 * sûr, mais `PL` peut être « place » ou une initiale) : en cas de doute, mieux
 * vaut ne pas normaliser et laisser la comparaison échouer sur une différence
 * visible, plutôt que fusionner à tort deux adresses distinctes.
 */
export const ADDRESS_ABBREVIATIONS: Record<string, string> = {
  // Types de voie
  av: 'avenue',
  ave: 'avenue',
  avn: 'avenue',
  bd: 'boulevard',
  bld: 'boulevard',
  blvd: 'boulevard',
  che: 'chemin',
  chem: 'chemin',
  // `R` et `TRA` relevés sur des justificatifs réels du parc (« 2B R DU TUNNEL »,
  // « 58 TRA FONTMERLE ») : ce sont les abréviations AFNOR de rue et traverse.
  r: 'rue',
  tra: 'traverse',
  pl: 'place',
  rte: 'route',
  imp: 'impasse',
  all: 'allee',
  sq: 'square',
  crs: 'cours',
  qu: 'quai',
  res: 'residence',
  resid: 'residence',
  lot: 'lotissement',
  ham: 'hameau',
  fbg: 'faubourg',
  pas: 'passage',
  sen: 'sentier',
  vla: 'villa',
  cite: 'cite',
  rpt: 'rond point',
  esp: 'esplanade',
  prom: 'promenade',
  // Titres et qualificatifs fréquents dans les noms de voie
  mal: 'marechal',
  mar: 'marechal',
  gal: 'general',
  gen: 'general',
  pdt: 'president',
  pst: 'president',
  st: 'saint',
  ste: 'sainte',
  sts: 'saints',
  dr: 'docteur',
  cdt: 'commandant',
  cne: 'capitaine',
  lt: 'lieutenant',
  cl: 'colonel',
  mgr: 'monseigneur',
  // Compléments d'adresse
  bat: 'batiment',
  apt: 'appartement',
  appt: 'appartement',
  esc: 'escalier',
  etg: 'etage',
  zi: 'zone industrielle',
  za: 'zone artisanale',
  zac: 'zone amenagement concerte',
};

/** Mots vides ignorés lors de la comparaison (articles, prépositions). */
const STOP_WORDS = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'l', 'd', 'a', 'au', 'aux', 'et', 'en']);

/**
 * Réduit une chaîne à sa forme comparable : minuscules, sans accent, sans
 * ponctuation, espaces normalisés.
 */
export const foldText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
};

/**
 * Normalise un libellé de voie : développe les abréviations connues et retire
 * les mots vides, pour que « AV DU MAL DE LATTRE » et « Avenue du Maréchal de
 * Lattre » se réduisent tous deux à « avenue marechal lattre ».
 */
export const normalizeStreetLabel = (value: unknown): string => {
  const folded = foldText(value);
  if (!folded) return '';
  return folded
    .split(' ')
    .map((word) => ADDRESS_ABBREVIATIONS[word] ?? word)
    .flatMap((word) => word.split(' '))
    .filter((word) => word.length > 0 && !STOP_WORDS.has(word))
    .join(' ');
};

/**
 * Normalise un TYPE de voie seul (« CHE », « Chemin », « RTE », « Route »…) en
 * développant l'abréviation AFNOR, pour le comparer indépendamment du nom de la
 * voie. Renvoie '' si la valeur est absente ou vide.
 */
export const normalizeStreetType = (value: unknown): string => {
  const folded = foldText(value);
  if (!folded) return '';
  return folded
    .split(' ')
    .map((word) => ADDRESS_ABBREVIATIONS[word] ?? word)
    .flatMap((word) => word.split(' '))
    .filter((word) => word.length > 0)
    .join(' ');
};

/** Normalise un nom de commune (même traitement, sans développement d'abréviations de voie). */
export const normalizeCity = (value: unknown): string => {
  const folded = foldText(value);
  if (!folded) return '';
  return folded
    .split(' ')
    .map((word) => (word === 'st' ? 'saint' : word === 'ste' ? 'sainte' : word))
    .filter((word) => word.length > 0 && !STOP_WORDS.has(word))
    .join(' ');
};

/** Normalise un code postal : 5 chiffres, ou '' si la valeur n'est pas exploitable. */
export const normalizePostalCode = (value: unknown): string => {
  const digits = String(value ?? '').replace(/\D/g, '');
  return digits.length === 5 ? digits : '';
};

/**
 * Normalise un numéro de voie en séparant le chiffre de son extension
 * (`12 bis` → `{ number: '12', extension: 'b' }`). L'extension est réduite à sa
 * première lettre : « bis » / « B » désignent la même chose selon le document.
 */
export const normalizeStreetNumber = (value: unknown): { number: string; extension: string } => {
  const folded = foldText(value);
  const match = folded.match(/^(\d+)\s*([a-z]*)/);
  if (!match) return { number: '', extension: '' };
  return { number: match[1], extension: (match[2] ?? '').charAt(0) };
};

/**
 * La distance d'édition entre `a` et `b` est-elle ≤ `max` ?
 *
 * Implémentation bornée (sortie anticipée dès que le seuil est dépassé) : on ne
 * cherche pas la distance exacte, seulement à distinguer « une coquille » de
 * « deux libellés différents ».
 */
export const levenshteinAtMost = (a: string, b: string, max: number): boolean => {
  if (Math.abs(a.length - b.length) > max) return false;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const curr = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
      curr.push(v);
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > max) return false;
    prev = curr;
  }
  return prev[b.length] <= max;
};

export interface ComparableAddress {
  streetNumber?: unknown;
  /**
   * TYPE de voie seul (« CHE », « Chemin », « ROUTE »…), comparé à part du nom.
   * Voir `compareAddresses` pour la raison.
   */
  streetType?: unknown;
  streetLabel?: unknown;
  postalCode?: unknown;
  city?: unknown;
}

export type AddressDivergence = 'code_postal' | 'commune' | 'numero_voie' | 'type_voie' | 'nom_voie';

/**
 * Compare deux adresses et renvoie la liste des composants réellement
 * divergents. Un composant absent d'un côté n'est JAMAIS compté comme
 * divergent : on ne conclut pas d'une donnée manquante (règle maîtresse du
 * référentiel CTVO — « donnée mal lue ≠ donnée manquante »).
 */
export const compareAddresses = (a: ComparableAddress, b: ComparableAddress): AddressDivergence[] => {
  const divergences: AddressDivergence[] = [];

  const cpA = normalizePostalCode(a.postalCode);
  const cpB = normalizePostalCode(b.postalCode);
  const cityA = normalizeCity(a.city);
  const cityB = normalizeCity(b.city);
  const cpDiffers = Boolean(cpA && cpB && cpA !== cpB);
  const cityDiffers = Boolean(cityA && cityB && cityA !== cityB);
  // La COMMUNE arbitre le code postal.
  //
  // Mesure sur le parc réel (78 dossiers de juillet 2026 portant les deux
  // pièces) : 33 présentaient un écart, dont la très grande majorité était un
  // code postal du Cerfa MANUSCRIT mal lu, avec une commune parfaitement
  // identique — 30700/30170 Blauzac, 29200/29120 Brest, 92600/92160 Asnières,
  // 77176/71711 Nandy. Dans tous ces cas la commune confirme qu'il s'agit du
  // MÊME lieu : l'écart vient de la transcription, pas de l'adresse.
  //
  // Signaler ça comme une divergence d'adresse produirait une alerte sur ~40 %
  // des dossiers — précisément le « toujours le même message qui pollue »
  // reproché par le pôle CG sur l'alerte VIN. Quand une seule des deux valeurs
  // diverge, l'autre fait foi et l'écart est traité comme un indice de
  // relecture (cf. `isTranscriptionOnly` renvoyé par `compareAddresses`).
  if (cpDiffers) divergences.push('code_postal');
  if (cityDiffers) divergences.push('commune');

  const numA = normalizeStreetNumber(a.streetNumber);
  const numB = normalizeStreetNumber(b.streetNumber);
  if (numA.number && numB.number && numA.number !== numB.number) {
    divergences.push('numero_voie');
  } else if (
    numA.number &&
    numB.number &&
    numA.extension &&
    numB.extension &&
    numA.extension !== numB.extension
  ) {
    divergences.push('numero_voie');
  }

  // Le TYPE de voie est comparé À PART du nom de la voie.
  //
  // Cas réel GL-911-WF (reg 12922, 2026-08-21) : la facture EDF porte « ROUTE DE
  // L ADRET », le Cerfa manuscrit « Chemin de l'Adret » — même numéro, même
  // commune, même nom de voie. Tant que le type était concaténé au nom avant
  // comparaison, cet écart de UN mot ressortait comme « le nom de la voie ne
  // correspond pas », c'est-à-dire l'alerte la plus grave de la règle, sur un
  // dossier où François a répondu « on est exactement sur la même adresse ».
  //
  // Les deux documents n'ont pas la même source : le justificatif reprend le
  // libellé du fournisseur (qui vaut ce qu'il vaut sur un hameau), le Cerfa est
  // rempli à la main par l'acquéreur. Le type de voie est donc le composant le
  // plus volatil de l'adresse — mais il part quand même à MiSIV
  // (`BuyerAddressType`), on ne peut pas simplement l'ignorer : il devient une
  // divergence à lui seul, que `isTranscriptionOnlyDivergence` rétrograde en
  // INFO quand le nom de la voie, lui, concorde.
  const typeA = normalizeStreetType(a.streetType);
  const typeB = normalizeStreetType(b.streetType);
  if (typeA && typeB && typeA !== typeB) {
    divergences.push('type_voie');
  }

  const streetA = normalizeStreetLabel(a.streetLabel);
  const streetB = normalizeStreetLabel(b.streetLabel);
  if (streetA && streetB && streetA !== streetB) {
    // Tolérance d'inclusion : un document peut porter le libellé complet et
    // l'autre une version tronquée (« marechal lattre tassigny » vs
    // « marechal lattre »). Ce n'est pas une adresse différente.
    const included = streetA.includes(streetB) || streetB.includes(streetA);
    // Tolérance d'UNE faute : « GEORGES BRAQUE » vs « GEORGE BRAQUE » (cas réel
    // du parc) est une coquille de recopie, pas une autre voie. Au-delà d'un
    // caractère, on considère que c'est bien un libellé différent.
    if (!included && levenshteinAtMost(streetA, streetB, 1) === false) {
      divergences.push('nom_voie');
    }
  }

  return divergences;
};

/**
 * L'écart constaté s'explique-t-il par une simple erreur de transcription
 * plutôt que par deux adresses réellement différentes ?
 *
 * Chaque divergence est jugée SÉPARÉMENT, et seulement sur la foi d'un TÉMOIN :
 * un autre composant, renseigné des deux côtés et concordant, qui désigne le
 * même lieu et contredit donc l'écart constaté.
 *
 *   code_postal → témoin : la commune (30700/30170 Blauzac, 29200/29120 Brest…)
 *   commune     → témoin : le code postal
 *   type_voie   → témoin : le nom de la voie (« ROUTE de l'Adret » / « Chemin
 *                 de l'Adret » — GL-911-WF)
 *
 * Le numéro et le nom de la voie n'ont AUCUN témoin : rien dans le reste de
 * l'adresse ne peut confirmer qu'un « 67 » lu « 57 », ou « avenue de Verdun »
 * lu « rue Lord Clive », désigne malgré tout le même lieu. Ils restent donc
 * toujours de vraies divergences.
 *
 * Le verdict n'est « transcription » que si CHAQUE divergence a son témoin :
 * code postal ET commune divergents ensemble s'invalident mutuellement, et
 * c'est bien une autre adresse.
 */
export const isTranscriptionOnlyDivergence = (
  divergences: AddressDivergence[],
  a: ComparableAddress,
  b: ComparableAddress,
): boolean => {
  if (divergences.length === 0) return false;
  const agrees = (normalize: (v: unknown) => string, key: keyof ComparableAddress): boolean => {
    const valueA = normalize(a[key]);
    const valueB = normalize(b[key]);
    return Boolean(valueA && valueB && valueA === valueB);
  };
  return divergences.every((divergence) => {
    if (divergence === 'code_postal') return agrees(normalizeCity, 'city');
    if (divergence === 'commune') return agrees(normalizePostalCode, 'postalCode');
    if (divergence === 'type_voie') return agrees(normalizeStreetLabel, 'streetLabel');
    return false;
  });
};

/** Libellés lisibles des composants divergents, pour le message d'alerte. */
export const DIVERGENCE_LABELS: Record<AddressDivergence, string> = {
  code_postal: 'le code postal',
  commune: 'la commune',
  numero_voie: 'le numéro de voie',
  type_voie: 'le type de voie',
  nom_voie: 'le nom de la voie',
};
